import AddIcon from "@mui/icons-material/Add";
import * as React from "react";

import { type SkillSummary } from "@/shared/domain/skills";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AddSkillMenu.module.css";

interface AddSkillMenuProps {
  /** Org-public skills available to attach — already filtered to exclude any
   *  the agent has selected, so picking one can never produce a duplicate. */
  options: SkillSummary[];
  /** True while the org skills query is still in flight. */
  loading: boolean;
  disabled?: boolean;
  onAdd: (skillId: string) => void;
}

/**
 * Plus-button menu that attaches one org-public skill to the agent.
 *
 * The goals editor hardcodes its menu options because goal types are a
 * fixed, code-defined set; skills are org data, so the options here come
 * from the live ``useSkills`` query (filtered to org-public, minus the
 * already-selected). Lives outside the skill list so the affordance is
 * present even when zero skills are attached.
 */
export function AddSkillMenu({
  options,
  loading,
  disabled,
  onAdd,
}: AddSkillMenuProps) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // Close the menu if the trigger becomes disabled while open, so the popover
  // can't outlive its affordance.
  React.useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: MouseEvent) => {
      if (
        anchorRef.current !== null &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Add skill
      </Button>
      {open && (
        <div className={styles.popover} role="menu" aria-label="Add skill">
          {loading && (
            <span className={styles.empty}>Loading skills&hellip;</span>
          )}
          {!loading && options.length === 0 && (
            <span className={styles.empty}>
              No org-public skills available to add.
            </span>
          )}
          {!loading &&
            options.map((option) => (
              <button
                key={option.skill.skill_id}
                type="button"
                role="menuitem"
                className={styles.option}
                onClick={() => {
                  onAdd(option.skill.skill_id);
                  setOpen(false);
                }}
              >
                <span className={styles.optionLabel}>{option.skill.name}</span>
                <span className={styles.optionDescription}>
                  {option.latest_version?.description ??
                    "No description for the latest version."}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
